import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  styled,
  Typography,
  useTheme
} from '@mui/material';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import useWorkspaceMember from 'src/hooks/useWorkspaceMember';
import internationalization from 'src/i18n/i18n';
import { ThemeContext } from 'src/theme/ThemeProvider';
import { urls } from 'src/urls';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding: ${theme.spacing(0, 1)};
        height: ${theme.spacing(7)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${theme.palette.secondary.light}
`
);

function HeaderUserbox() {
  const { t }: { t: any } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { role, locationName } = useWorkspaceMember();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose();
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  const stringAvatar = (userName: string) => {
    return `${userName.split(' ')[0][0]}${userName.split(' ')[1][0]}`;
  };

  return (
    <>
      <UserBoxButton color="primary" ref={ref} onClick={handleOpen}>
        <Avatar
          variant="rounded"
          src={user.avatar}
          sx={{
            bgcolor: alpha(theme.colors.info.light, 0.2),
            color: theme.colors.info.main
          }}
        >
          {stringAvatar(user.name)}
        </Avatar>
        <Box
          component="span"
          sx={{
            display: { xs: 'none', lg: 'inline-block' }
          }}
        >
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {t(role)} {locationName ? ` (${locationName})` : ''}
            </UserBoxDescription>
          </UserBoxText>
        </Box>
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'flex' }
          }}
          alignItems="center"
        >
          <ExpandMoreIcon
            color="secondary"
            fontSize="small"
            sx={{
              ml: 1
            }}
          />
        </Box>
      </UserBoxButton>
      <Popover
        disableScrollLock
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox
          sx={{
            minWidth: 280
          }}
          display="flex"
        >
          <Avatar
            variant="rounded"
            src={user.avatar}
            sx={{
              bgcolor: alpha(theme.colors.info.light, 0.2),
              color: theme.colors.info.main
            }}
          >
            {stringAvatar(user.name)}
          </Avatar>

          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {t(role)} {locationName ? ` (${locationName})` : ''}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem>
            <ListItemText primary={t('Language')} />
            <LanguageDropdown />
          </ListItem>
          <ListItem>
            <ListItemText primary={t('Theme')} />
            <ThemeDropdown />
          </ListItem>
        </List>

        <Divider />
        <Box sx={{ m: 1, display: { xs: 'block', md: 'none' } }}>
          <Button color="primary" fullWidth href={urls.helpCenter}>
            <HelpIcon
              sx={{
                mr: 1
              }}
            />
            {t('Help & documentation')}
          </Button>
        </Box>
        <Box sx={{ m: 1 }}>
          <Button color="primary" fullWidth onClick={handleLogout}>
            <LockOpenIcon
              sx={{
                mr: 1
              }}
            />
            {t('Sign out')}
          </Button>
        </Box>
        <Divider />
        <Box display="flex" p={2} justifyContent="center">
          <Typography variant="subtitle1">
            <Link color="inherit" href={urls.termsOfService}>
              {t('Terms of Service')}
            </Link>
            {' | '}
            <Link color="inherit" href={urls.privacyPolicy}>
              {t('Privacy Policy')}
            </Link>
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;

const ImageWrapper = styled('img')(
  ({ theme }) => `
        width: 24px;
        margin-right: ${theme.spacing(1)};
        border-radius: 2px;
`
);

const SelectItem = styled(MenuItem)(
  ({ theme }) => `
        display: flex;
        align-items: center;
        color: 'red'
`
);

function PopoverSelectWrapper({ children }) {
  // Makes the select component smaller
  return (
    <FormControl
      sx={{
        minWidth: 144,
        '.MuiSelect-select': {
          display: 'flex',
          alignItems: 'center'
        }
      }}
      size="small"
    >
      {children}
    </FormControl>
  );
}

function LanguageDropdown() {
  const { i18n } = useTranslation();
  const getLanguage = i18n.language.split('-')[0];

  const switchLanguage = ({ lng }: { lng: any }) =>
    internationalization.changeLanguage(lng);

  const languages = [
    {
      code: 'en',
      name: 'English',
      icon: '/static/images/flags/GB.svg'
    },
    {
      code: 'ua',
      name: 'Україна',
      icon: '/static/images/flags/UA.svg'
    },
    {
      code: 'fr',
      name: 'Français',
      icon: '/static/images/flags/FR.svg'
    },
    {
      code: 'nl',
      name: 'Nederlands',
      icon: '/static/images/flags/NL.svg'
    }
    // {
    //   code: 'ar',
    //   name: 'Arabic',
    //   icon: aeFlag
    // }
  ];

  return (
    <PopoverSelectWrapper>
      <Select
        id="language-select"
        value={getLanguage}
        displayEmpty
        onChange={(e) => switchLanguage({ lng: e.target.value })}
      >
        {languages.map((l) => (
          <SelectItem key={l.code} value={l.code}>
            <ImageWrapper alt={l.name} src={l.icon} />
            <span>{l.name}</span>
          </SelectItem>
        ))}
      </Select>
    </PopoverSelectWrapper>
  );
}

function ThemeDropdown() {
  const themes = [
    {
      title: 'Captur',
      slug: 'CapturTheme'
    },
    {
      title: 'Captur Old',
      slug: 'CapturOldTheme'
    },
    {
      title: 'Grey Goose',
      slug: 'GreyGooseTheme'
    },
    {
      title: 'Purple Flow',
      slug: 'PurpleFlowTheme'
    },
    {
      title: 'Nebula Fighter',
      slug: 'NebulaFighterTheme'
    },
    {
      title: 'Green Fields',
      slug: 'GreenFieldsTheme'
    },
    {
      title: 'Dark Spaces',
      slug: 'DarkSpacesTheme'
    }
  ];

  const setThemeName = useContext(ThemeContext);
  const curThemeName = localStorage.getItem('appTheme') || 'CapturOldTheme';

  const [theme, setTheme] = useState(curThemeName);

  const changeTheme = (theme): void => {
    setTheme(theme);
    setThemeName(theme);
  };

  return (
    <PopoverSelectWrapper>
      <Select
        id="theme-select"
        value={theme}
        displayEmpty
        onChange={(e) => changeTheme(e.target.value)}
        autoWidth={false}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {themes.map((theme) => (
          <SelectItem key={theme.slug} value={theme.slug}>
            {theme.title}
          </SelectItem>
        ))}
      </Select>
    </PopoverSelectWrapper>
  );
}
